import { useEffect, useState } from 'react'

import { ALERTS_UPDATED_EVENT, fetchAlertEvents } from '../api/alerts'
import { fetchSchedules } from '../api/schedules'
import { fetchTodos, TODOS_UPDATED_EVENT } from '../api/todos'

const IS_TEST = import.meta.env.MODE === 'test'

export interface UnseenCounts {
  todos: number
  schedules: number
  alerts: number
}

interface UseUnseenCountsOptions {
  enabled?: boolean
  intervalMs?: number
}

export function useUnseenCounts(
  refreshKey: unknown = null,
  { enabled = !IS_TEST, intervalMs = 10_000 }: UseUnseenCountsOptions = {},
): UnseenCounts {
  const [counts, setCounts] = useState<UnseenCounts>({ todos: 0, schedules: 0, alerts: 0 })

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    let controller: AbortController | null = null

    function refresh() {
      controller?.abort()
      controller = new AbortController()
      const activeController = controller
      Promise.allSettled([
        fetchTodos(activeController.signal),
        fetchSchedules(activeController.signal, false),
        fetchAlertEvents(activeController.signal),
      ])
        .then(([todosResult, schedulesResult, alertsResult]) => {
          if (cancelled || activeController.signal.aborted) return
          const todos = todosResult.status === 'fulfilled'
            ? todosResult.value.active.filter((item) => !item.seen_at).length
            : 0
          const schedules = schedulesResult.status === 'fulfilled'
            ? schedulesResult.value.jobs.filter((item) => !item.seen_at).length
            : 0
          // allSettled, so an alerts-service or DB hiccup zeroes only this
          // badge instead of blanking the whole sidebar.
          const alerts = alertsResult.status === 'fulfilled' ? alertsResult.value.unseen : 0
          setCounts({ todos, schedules, alerts })
        })
        .catch(() => {
          if (!cancelled && !activeController.signal.aborted) setCounts({ todos: 0, schedules: 0, alerts: 0 })
        })
    }

    refresh()
    const intervalId = intervalMs > 0 ? window.setInterval(refresh, intervalMs) : null
    window.addEventListener(TODOS_UPDATED_EVENT, refresh)
    window.addEventListener('credem:schedules-updated', refresh)
    window.addEventListener(ALERTS_UPDATED_EVENT, refresh)
    window.addEventListener('focus', refresh)
    return () => {
      cancelled = true
      controller?.abort()
      if (intervalId !== null) window.clearInterval(intervalId)
      window.removeEventListener(TODOS_UPDATED_EVENT, refresh)
      window.removeEventListener('credem:schedules-updated', refresh)
    window.removeEventListener(ALERTS_UPDATED_EVENT, refresh)
      window.removeEventListener('focus', refresh)
    }
  }, [enabled, intervalMs, refreshKey])

  return counts
}
